'use client';

// Vite bakes these at build time via `define` in vite.config.ts.
const BAKED_API_KEY = process.env.CHURCH_HUB_API_KEY ?? '';
const BAKED_APP_SLUG = process.env.CHURCH_HUB_APP_SLUG ?? '';

function chubHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  if (BAKED_API_KEY) h['x-api-key'] = BAKED_API_KEY;
  if (BAKED_APP_SLUG) h['x-app-slug'] = BAKED_APP_SLUG;
  return h;
}

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Accessibility,
  Baby,
  Backpack,
  ChevronDown,
  ChevronRight,
  Coffee,
  Droplets,
  GraduationCap,
  Hand,
  HandHeart,
  Heart,
  Languages,
  List,
  Map as MapIcon,
  MapPin,
  Phone,
  Radio,
  Search,
  Tv,
  Video,
  Wheat,
} from 'lucide-react';
import { LiveSheet } from './LiveSheet';
import { mpFetch } from '@church/nextjs-ui/website/mp-session';

const TEAL = '#299c8f';
const DARK = '#1c2b39';

// --- Types ---

export interface Amenity {
  id: number;
  name: string;
  icon: string; // FontAwesome icon name from DB (e.g. "fa-baby")
  color: string | null;
  detail: string | null;
}

export interface ServiceTime {
  time: string;
  amenities: Amenity[];
}

export interface SpecialService {
  name: string;
  description?: string | null;
  services: ServiceTime[];
  venue_name?: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude?: number;
  longitude?: number;
  image_url?: string | null;
  url?: string | null;
  event_id?: number | null;
}

export interface Congregation {
  id: number;
  name: string;
  display_name: string;
  short_name?: string | null;
  description: string | null;
  campus_slug: string | null;
  pastor: { name: string | null; image_url: string | null };
  service_times: string;
  services: ServiceTime[];
  special_service: SpecialService | null;
  staff_directory_url: string | null;
}

export interface LocationGroup {
  id: number;
  primary_congregation_id: number | null;
  phone: string;
  svg_url: string | null;
  address: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  congregations: Congregation[];
}

export function getPrimary(group: LocationGroup): Congregation {
  if (group.primary_congregation_id) {
    const found = group.congregations.find((c) => c.id === group.primary_congregation_id);
    if (found) return found;
  }
  return group.congregations[0];
}

export function getSubCongregations(group: LocationGroup): Congregation[] {
  const primaryId = group.primary_congregation_id ?? group.congregations[0]?.id;
  return group.congregations.filter((c) => c.id !== primaryId);
}

/**
 * Compute legend details scoped to a single congregation's services.
 * If all instances of an amenity have the same detail, returns that detail.
 * Otherwise returns null (detail should show inline on each service time).
 */
export function computeScopedLegendDetails(cong: Congregation): Map<number, string | null> {
  const detailSets = new Map<number, Set<string | null>>();
  cong.services.forEach((svc) =>
    svc.amenities.forEach((a) => {
      if (!detailSets.has(a.id)) detailSets.set(a.id, new Set());
      detailSets.get(a.id)!.add(a.detail ?? null);
    })
  );
  const result = new Map<number, string | null>();
  for (const [id, values] of detailSets) {
    if (values.size === 1) {
      result.set(id, values.values().next().value ?? null);
    } else {
      result.set(id, null);
    }
  }
  return result;
}

export interface LocationsFinderProps {
  apiBaseUrl?: string;
  googleMapsApiKey?: string;
  viewLocationBaseUrl?: string;
  selectedCongregationId?: number | null;
  /**
   * Start with the "Live now" chip on, so the list opens on the campuses
   * streaming right now (the /live page). Visitors can still turn it off.
   * If nothing is live once the first poll returns, the filter lets go and
   * the full list shows. Off by default: /locations wants every campus.
   */
  liveOnly?: boolean;
}

// --- Icon mapping (FontAwesome name from DB → Lucide component) ---

export const ICON_MAP: Record<
  string,
  React.ComponentType<{ className?: string; style?: React.CSSProperties }>
> = {
  // Lucide names (preferred)
  Baby,
  Coffee,
  Languages,
  Accessibility,
  // Livestream: the Amenities lookup uses "Radio"; Video and Tv are accepted too
  Radio,
  Tv,
  Video,
  // Students, Special Needs, ASL (Amenities trim, 2026-09-20)
  // Communion, Baptism (2026-09-20)
  Wheat,
  Droplets,
  Backpack,
  GraduationCap,
  Hand,
  HandHeart,
  // Legacy fa- prefixed names
  'fa-baby': Baby,
  'fa-coffee': Coffee,
  'fa-mug-hot': Coffee,
  'fa-language': Languages,
  'fa-wheelchair': Accessibility,
  'fa-universal-access': Accessibility,
  'fa-broadcast-tower': Radio,
  'fa-video': Video,
  'fa-tv': Tv,
};

export const DEFAULT_AMENITY_COLOR = '#6B7280';

const DISTANCE_OPTIONS = [5, 10, 25, 50, 100];

const CAMPUS_COOKIE = 'woodside-selected-campus';

/** The header picker's cookie, for first paint when the page did not pass a campus in */
function readCookieCampusId(): number | null {
  if (typeof document === 'undefined') return null;
  try {
    const raw = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${CAMPUS_COOKIE}=`));
    if (!raw) return null;
    const data = JSON.parse(decodeURIComponent(raw.slice(CAMPUS_COOKIE.length + 1)));
    return data?.congregationId ? Number(data.congregationId) : null;
  } catch {
    return null;
  }
}

// --- Helpers ---

function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function loadGoogleMaps(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.Map) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          resolve();
        }
      }, 50);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    };
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

// Free geocoding fallback using the Nominatim API (OpenStreetMap)
async function geocodeZip(zip: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(zip)}&country=US&format=json&limit=1`
    );
    const data = await res.json();
    if (data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
  } catch {
    // Nominatim failed
  }

  return null;
}

// --- Live status (Sardius) ---

interface CampusLive {
  isLive: boolean;
  watchUrl: string;
}

/** Pulsing "LIVE" pill linking to the campus's stream. Renders nothing when off air. */
function LiveBadge({ live }: { live?: CampusLive }) {
  if (!live?.isLive) return null;
  return (
    <a
      href={live.watchUrl}
      target="_blank"
      rel="noopener noreferrer"
      // The row around it navigates to the campus page on click and Enter;
      // the pill's own navigation has to be the only one that happens.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-bold tracking-wider text-white uppercase no-underline transition-transform hover:scale-105 active:scale-95"
      title="Watch live now"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
      </span>
      Live
    </a>
  );
}

// --- Amenity badge ---

function AmenityBadge({
  amenity,
  size = 'sm',
  light = false,
}: {
  amenity: Amenity;
  size?: 'sm' | 'md';
  /** White ring + icon, for sitting on a navy chip */
  light?: boolean;
}) {
  const Icon = ICON_MAP[amenity.icon];
  if (!Icon) return null;

  const color = light ? 'white' : amenity.color || DEFAULT_AMENITY_COLOR;
  const sizeClass = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <div
      className={`${sizeClass} flex items-center justify-center rounded-full`}
      style={{ border: `1.5px solid ${color}` }}
      title={amenity.detail ? `${amenity.name} (${amenity.detail})` : amenity.name}
    >
      <Icon className={iconSize} style={{ color }} />
    </div>
  );
}

// --- Service time with amenities ---

function parseDay(time: string): { day: string; timeOnly: string } {
  const match = time.match(/^(SUN|MON|TUE|WED|THU|FRI|SAT)\s+(.+)$/i);
  if (match) return { day: match[1].toUpperCase(), timeOnly: match[2] };
  return { day: '', timeOnly: time };
}

function ServiceTimeLine({
  services,
  amenityFilters,
  legendDetails,
  light = false,
}: {
  services: ServiceTime[];
  amenityFilters?: Set<number>;
  legendDetails?: Map<number, string | null>;
  light?: boolean;
}) {
  const hasFilters = amenityFilters && amenityFilters.size > 0;

  // Group services by day
  const dayGroups: { day: string; items: ServiceTime[] }[] = [];
  let currentDay = '';
  for (const svc of services) {
    const { day } = parseDay(svc.time);
    const effectiveDay = day || currentDay;
    if (effectiveDay !== currentDay || dayGroups.length === 0) {
      dayGroups.push({ day: effectiveDay, items: [svc] });
      currentDay = effectiveDay;
    } else {
      dayGroups[dayGroups.length - 1].items.push(svc);
    }
  }

  return (
    <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-0.5">
      {dayGroups.map((group, gi) => (
        <p
          key={gi}
          className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] leading-snug"
        >
          {group.day && (
            <span
              className={`mr-1 text-[9.5px] font-bold tracking-wider uppercase ${light ? 'text-white/40' : 'text-gray-400'}`}
            >
              {group.day}
            </span>
          )}
          {group.items.map((svc, i) => {
            const svcAmenityIds = svc.amenities.map((a) => a.id);
            const matches =
              !hasFilters || Array.from(amenityFilters).every((f) => svcAmenityIds.includes(f));
            const { timeOnly } = parseDay(svc.time);
            return (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span className={light ? 'text-white/40' : 'text-gray-400'} aria-hidden="true">
                    ·
                  </span>
                )}
                <span
                  className="inline-flex items-center gap-1 transition-opacity"
                  style={{ opacity: hasFilters && !matches ? 0.3 : 1 }}
                >
                  <span className="font-semibold" style={{ color: light ? 'white' : DARK }}>
                    {timeOnly}
                  </span>
                  {svc.amenities.length > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      {svc.amenities.map((a) => {
                        // Only show detail inline when legendDetails is provided and detail differs
                        const legendDetail = legendDetails?.get(a.id);
                        const showDetail = legendDetails && a.detail && a.detail !== legendDetail;
                        return (
                          <span key={a.id} className="inline-flex items-center gap-0.5">
                            <AmenityBadge amenity={a} />
                            {showDetail && (
                              <span
                                className={`text-[10px] ${light ? 'text-white/50' : 'text-gray-400'}`}
                              >
                                {a.detail}
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </span>
                  )}
                </span>
              </React.Fragment>
            );
          })}
        </p>
      ))}
    </div>
  );
}

// --- Campus pastor, avatar + name (the header panel shows the name on hover; a list has room for it) ---

function PastorLine({ name, imageUrl }: { name: string; imageUrl: string | null }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span className="flex flex-shrink-0 items-center gap-2" title={`Campus Pastor ${name}`}>
      <span className="hidden flex-col items-end text-right sm:flex">
        <span className="text-[8.5px] leading-[11px] font-bold tracking-wider text-gray-400 uppercase">
          Campus Pastor
        </span>
        <span className="text-[11px] leading-[14px] font-semibold" style={{ color: DARK }}>
          {name}
        </span>
      </span>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className="h-9 w-9 rounded-full object-cover ring-2 ring-white"
          loading="lazy"
        />
      ) : (
        <span
          className="flex h-9 w-9 items-center justify-center rounded-full text-[10px] font-bold text-white ring-2 ring-white"
          style={{ background: DARK }}
          aria-label={name}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

// --- One congregation in a list row: art, name, pastor, times, heart ---

function CongregationRow({
  cong,
  svgUrl,
  isSelected,
  live,
  amenityFilters,
  onSelect,
  nested = false,
}: {
  cong: Congregation;
  /** The location's art, on the first congregation only */
  svgUrl: string | null;
  isSelected: boolean;
  live?: CampusLive;
  amenityFilters: Set<number>;
  /** The heart: make this your campus */
  onSelect: () => void;
  /** A second congregation at the location, indented under the art column */
  nested?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* Name row: art (first congregation only), name, live, pastor */}
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          {!nested &&
            (svgUrl ? (
              <img src={svgUrl} alt="" className="h-10 w-10 object-contain" />
            ) : (
              <MapPin className="h-5 w-5 text-gray-400" />
            ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3
            className={`font-display m-0 leading-none tracking-wide transition-colors group-hover/row:text-[#299c8f] ${
              nested ? 'text-[22px]' : 'text-[26px]'
            }`}
            style={{ color: DARK }}
          >
            {cong.display_name}
          </h3>
          <LiveBadge live={live} />
        </div>
        {cong.pastor.name && (
          <PastorLine name={cong.pastor.name} imageUrl={cong.pastor.image_url} />
        )}
      </div>

      {/* Service times, the heart, and a chevron that says the row opens */}
      {!cong.special_service && (
        <div className="flex min-h-[28px] items-center justify-between gap-3 pl-[52px]">
          <ServiceTimeLine services={cong.services} amenityFilters={amenityFilters} />
          <span className="-mr-1.5 flex flex-shrink-0 items-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!isSelected) onSelect();
              }}
              aria-label={isSelected ? 'Your campus' : `Set ${cong.name} as my campus`}
              aria-pressed={isSelected}
              title={isSelected ? 'Your campus' : 'Set as my campus'}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-transparent p-0 transition-transform hover:scale-110 active:scale-95"
            >
              <Heart
                className="h-[18px] w-[18px]"
                fill={isSelected ? TEAL : 'none'}
                style={{ color: isSelected ? TEAL : '#9ca3af' }}
              />
            </button>
            <span
              className="flex h-8 w-8 items-center justify-center transition-transform group-hover/row:translate-x-0.5"
              aria-hidden="true"
            >
              <ChevronRight className="h-4 w-4" style={{ color: DARK }} />
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

// --- Inline scroll indicator (avoids shadow DOM / CSS bundling issues with shared UI) ---

function ListScrollIndicator({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function check() {
      if (!el) return;
      const hasMore = el.scrollHeight > el.clientHeight + 20;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20;
      setShow(hasMore && !atBottom);
    }

    check();
    const t = setTimeout(check, 300);
    el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [containerRef]);

  if (!show) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 64,
        background: 'linear-gradient(to top, white, transparent)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      <div
        style={{
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          color: '#9ca3af',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          Scroll for more
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </div>
  );
}

// --- Canvas marker from SVG URL ---

export const markerCache = new Map<string, string>();

export function createSvgMarker(
  svgUrl: string,
  borderColor: string,
  size: number
): Promise<string> {
  const cacheKey = `${svgUrl}:${borderColor}:${size}`;
  const cached = markerCache.get(cacheKey);
  if (cached) return Promise.resolve(cached);

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const padding = 6;
      const pointerHeight = 10;
      const cardW = size;
      const cardH = size;
      const totalW = cardW + padding * 2;
      const totalH = cardH + padding * 2 + pointerHeight;
      const r = 8; // border radius

      const canvas = document.createElement('canvas');
      const dpr = window.devicePixelRatio || 1;
      canvas.width = totalW * dpr;
      canvas.height = totalH * dpr;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(dpr, dpr);

      // Shadow
      ctx.shadowColor = 'rgba(0,0,0,0.2)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;

      // Rounded rect + pointer path
      const x = padding;
      const y = padding;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + cardW - r, y);
      ctx.quadraticCurveTo(x + cardW, y, x + cardW, y + r);
      ctx.lineTo(x + cardW, y + cardH - r);
      ctx.quadraticCurveTo(x + cardW, y + cardH, x + cardW - r, y + cardH);
      // Pointer
      ctx.lineTo(totalW / 2 + 6, y + cardH);
      ctx.lineTo(totalW / 2, y + cardH + pointerHeight);
      ctx.lineTo(totalW / 2 - 6, y + cardH);
      ctx.lineTo(x + r, y + cardH);
      ctx.quadraticCurveTo(x, y + cardH, x, y + cardH - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();

      ctx.fillStyle = 'white';
      ctx.fill();

      // Border
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Clip and draw SVG image
      ctx.save();
      ctx.beginPath();
      const inset = 3;
      const ir = r - 1;
      ctx.moveTo(x + inset + ir, y + inset);
      ctx.lineTo(x + cardW - inset - ir, y + inset);
      ctx.quadraticCurveTo(x + cardW - inset, y + inset, x + cardW - inset, y + inset + ir);
      ctx.lineTo(x + cardW - inset, y + cardH - inset - ir);
      ctx.quadraticCurveTo(
        x + cardW - inset,
        y + cardH - inset,
        x + cardW - inset - ir,
        y + cardH - inset
      );
      ctx.lineTo(x + inset + ir, y + cardH - inset);
      ctx.quadraticCurveTo(x + inset, y + cardH - inset, x + inset, y + cardH - inset - ir);
      ctx.lineTo(x + inset, y + inset + ir);
      ctx.quadraticCurveTo(x + inset, y + inset, x + inset + ir, y + inset);
      ctx.closePath();
      ctx.clip();

      const drawW = cardW - inset * 2;
      const drawH = cardH - inset * 2;
      // Fit the SVG proportionally
      const scale = Math.max(drawW / img.width, drawH / img.height);
      const sw = img.width * scale;
      const sh = img.height * scale;
      ctx.drawImage(img, x + inset + (drawW - sw) / 2, y + inset + (drawH - sh) / 2, sw, sh);
      ctx.restore();

      const url = canvas.toDataURL();
      markerCache.set(cacheKey, url);
      resolve(url);
    };
    img.onerror = () => resolve(''); // fallback — will use default pin
    img.src = svgUrl;
  });
}

// --- Main component ---

export function LocationsFinder({
  apiBaseUrl = '',
  googleMapsApiKey = '',
  viewLocationBaseUrl = 'https://woodsidebible.org/locations',
  selectedCongregationId = null,
  liveOnly: liveOnlyDefault = false,
}: LocationsFinderProps) {
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // Congregation_ID -> Sardius live status (only campuses currently on air).
  const [liveByCampus, setLiveByCampus] = useState<Map<number, CampusLive>>(new Map());

  const [zipCode, setZipCode] = useState('');
  const [distance, setDistance] = useState(10);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [homeCampusId, setHomeCampusId] = useState<number | null>(
    () => selectedCongregationId ?? readCookieCampusId()
  );
  const [activeAmenityFilters, setActiveAmenityFilters] = useState<Set<number>>(new Set());
  // "Live now" chip: only campuses currently streaming
  const [liveOnly, setLiveOnly] = useState(liveOnlyDefault);
  // The first live-status poll has answered; before that, the count is unknown
  const [liveLoaded, setLiveLoaded] = useState(false);
  // A live campus whose row was tapped: the sheet asks stream or page. The
  // campus stays in state after closing so the sheet can animate out, and so
  // the sheet toggles `open` like the group finder's do instead of mounting
  // already open.
  const [liveSheet, setLiveSheet] = useState<{ cong: Congregation; group: LocationGroup } | null>(
    null
  );
  const [liveSheetOpen, setLiveSheetOpen] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  // Listen for campus changes from the campus-picker widget
  useEffect(() => {
    function handleCampusChange(e: Event) {
      const detail = (e as CustomEvent).detail;
      if (detail?.congregationId) {
        setHomeCampusId(detail.congregationId);
        setActiveGroupKey(null); // reset so the useEffect re-selects the group
      }
    }
    window.addEventListener('campus-picker:changed', handleCampusChange);
    return () => window.removeEventListener('campus-picker:changed', handleCampusChange);
  }, []);

  // The heart. Writes the cookie now and MinistryPlatform in the background when
  // signed in, the same as the header picker; the campus-picker:changed event
  // is what the header listens for, so nothing reloads.
  const handleSetHome = useCallback(
    (congregationId: number) => {
      setHomeCampusId(congregationId);

      let cong: Congregation | undefined;
      let svgUrl: string | null = null;
      for (const group of locations) {
        cong = group.congregations.find((c) => c.id === congregationId);
        if (cong) {
          svgUrl = group.svg_url;
          break;
        }
      }
      if (!cong) return;

      const slug = cong.campus_slug ?? cong.name.toLowerCase().replace(/\s+/g, '-');
      const campus = {
        congregationId,
        name: cong.name,
        shortName: cong.short_name ?? null,
        slug,
        svgUrl,
      };
      const host = window.location.hostname;
      const domain = host.endsWith('woodsidebible.org') ? ';domain=.woodsidebible.org' : '';
      const maxAge = 60 * 60 * 24 * 365;
      try {
        document.cookie = `${CAMPUS_COOKIE}=${encodeURIComponent(JSON.stringify(campus))};path=/;max-age=${maxAge};SameSite=Lax${domain}`;
      } catch {
        /* cookie blocked */
      }
      window.dispatchEvent(new CustomEvent('campus-picker:changed', { detail: campus }));

      const token = localStorage.getItem('mpp-widgets_AuthToken');
      if (!token || token === 'null') return;
      mpFetch(`${apiBaseUrl}/api/set-home-campus`, {
        method: 'POST',
        headers: {
          ...chubHeaders(),
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ congregationId }),
      })
        .then((res) => {
          if (!res.ok) console.error('Failed to set home campus:', res.status);
        })
        .catch((err) => console.error('Error setting home campus:', err));
    },
    [apiBaseUrl, locations]
  );

  // Collect unique amenities and determine legend details
  // If every instance of an amenity has the same detail, show it in the legend.
  // Otherwise, detail only appears at the service-time level.
  const { allAmenities, legendDetails } = useMemo(() => {
    const map = new Map<number, Amenity>();
    // Track all detail values per amenity id
    const detailSets = new Map<number, Set<string | null>>();

    const source = filteredLocations.length > 0 ? filteredLocations : locations;
    source.forEach((group) =>
      group.congregations.forEach((cong) =>
        cong.services.forEach((svc) =>
          svc.amenities.forEach((a) => {
            if (!map.has(a.id)) map.set(a.id, a);
            if (!detailSets.has(a.id)) detailSets.set(a.id, new Set());
            detailSets.get(a.id)!.add(a.detail ?? null);
          })
        )
      )
    );

    // If all instances have the same non-null detail, use it for the legend
    const details = new Map<number, string | null>();
    for (const [id, values] of detailSets) {
      if (values.size === 1) {
        const only = values.values().next().value;
        details.set(id, only ?? null);
      } else {
        details.set(id, null);
      }
    }

    return { allAmenities: Array.from(map.values()), legendDetails: details };
  }, [locations, filteredLocations]);

  // How many campuses are on air right now. The "Live now" chip exists only
  // while this is above zero, and a live-only filter lets go when it drops
  // back, so the list never stays narrowed by a chip that is no longer there.
  const liveCount = useMemo(() => {
    let n = 0;
    for (const group of locations)
      for (const cong of group.congregations) if (liveByCampus.get(cong.id)?.isLive) n += 1;
    return n;
  }, [locations, liveByCampus]);
  // Both fetches have to be in before the count means anything: the live poll
  // often answers before the campus list, and a zero against an empty list
  // would release a filter that was about to have two campuses in it.
  useEffect(() => {
    if (liveOnly && liveLoaded && locations.length > 0 && liveCount === 0) setLiveOnly(false);
  }, [liveOnly, liveLoaded, locations.length, liveCount]);

  // Apply the chip filters: live-only keeps groups with a campus on air;
  // amenities keep groups that have at least one congregation with ALL selected amenities
  const displayGroups = useMemo(() => {
    let result = filteredLocations;
    if (liveOnly) {
      result = result.filter((group) =>
        group.congregations.some((cong) => liveByCampus.get(cong.id)?.isLive)
      );
    }
    if (activeAmenityFilters.size === 0) return result;
    const filters = Array.from(activeAmenityFilters);
    return result.filter((group) =>
      group.congregations.some((cong) =>
        cong.services.some((svc) => {
          const svcIds = svc.amenities.map((a) => a.id);
          return filters.every((f) => svcIds.includes(f));
        })
      )
    );
  }, [filteredLocations, activeAmenityFilters, liveOnly, liveByCampus]);

  // Your campus leads the list. The order is fixed once we know the campus so
  // tapping a heart never shuffles the rows under your finger; the next page
  // load puts the new pick first.
  const pinnedIdRef = useRef<number | null>(null);
  if (pinnedIdRef.current == null && homeCampusId != null && locations.length > 0) {
    pinnedIdRef.current = homeCampusId;
  }
  const groups = useMemo(() => {
    const pinned = pinnedIdRef.current;
    if (!pinned) return displayGroups;
    return [...displayGroups].sort((a, b) => {
      const aHasSelected = a.congregations.some((c) => c.id === pinned);
      const bHasSelected = b.congregations.some((c) => c.id === pinned);
      if (aHasSelected && !bHasSelected) return -1;
      if (!aHasSelected && bHasSelected) return 1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayGroups, homeCampusId, locations]);

  // Set active group when selected congregation changes
  useEffect(() => {
    if (!homeCampusId || groups.length === 0) return;
    const match = groups.find((g) => g.congregations.some((c) => c.id === homeCampusId));
    if (match) setActiveGroupKey(String(match.id));
  }, [homeCampusId, groups]);

  // Fetch locations
  useEffect(() => {
    let cancelled = false;
    async function fetchLocations() {
      try {
        const res = await fetch(`${apiBaseUrl}/api/locations`, { headers: chubHeaders() });
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        const data: LocationGroup[] = await res.json();
        if (!cancelled) {
          if (data.length > 0) {
            setLocations(data);
          } else {
            setFetchError('No locations returned from API.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setFetchError(`Failed to load locations. ${err instanceof Error ? err.message : ''}`);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchLocations();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]);

  // Poll Sardius live status for all mapped campuses (one batched call).
  useEffect(() => {
    let cancelled = false;
    async function fetchLive() {
      try {
        const res = await fetch(`${apiBaseUrl}/api/live-status?all=1`, { headers: chubHeaders() });
        if (!res.ok) return;
        const data: {
          campuses?: Array<{ congregationId: number; isLive: boolean; watchUrl: string }>;
        } = await res.json();
        if (cancelled || !data.campuses) return;
        const map = new Map<number, CampusLive>();
        for (const c of data.campuses) {
          map.set(c.congregationId, { isLive: c.isLive, watchUrl: c.watchUrl });
        }
        setLiveByCampus(map);
        setLiveLoaded(true);
      } catch {
        // Live status is non-critical — leave the badge off on failure.
      }
    }
    fetchLive();
    const interval = setInterval(fetchLive, 45_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    setFilteredLocations(locations);
  }, [locations]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || groups.length === 0) return;

    async function initMap() {
      if (googleMapsApiKey) {
        try {
          await loadGoogleMaps(googleMapsApiKey);
        } catch {
          return;
        }
      }

      if (!window.google?.maps?.Map || !mapContainerRef.current) return;

      const center = { lat: 42.55, lng: -83.2 };

      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(mapContainerRef.current, {
          center,
          zoom: 8,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          keyboardShortcuts: false,
          styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
        });
      }

      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];

      const fallbackIcon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          `<svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="${TEAL}"/>
            <circle cx="16" cy="16" r="8" fill="white"/>
            <circle cx="16" cy="16" r="4" fill="${TEAL}"/>
          </svg>`
        )}`,
        scaledSize: new google.maps.Size(32, 42),
        anchor: new google.maps.Point(16, 42),
      };

      const MARKER_SIZE = 48;

      const bounds = new google.maps.LatLngBounds();

      const ACTIVE_SIZE = 56;

      const selectedGroup = homeCampusId
        ? groups.find((g) => g.congregations.some((c) => c.id === homeCampusId))
        : null;

      groups.forEach((group) => {
        const position = { lat: group.latitude, lng: group.longitude };
        bounds.extend(position);

        const isInitialActive = selectedGroup?.id === group.id;

        const marker = new google.maps.Marker({
          map: mapRef.current,
          position,
          title: group.congregations.map((c) => c.name).join(' / '),
          icon: fallbackIcon,
          zIndex: isInitialActive ? 999 : undefined,
        });

        // If there's an SVG URL, load and apply it (with active styling if selected)
        const svgUrl = group.svg_url;
        if (svgUrl) {
          const size = isInitialActive ? ACTIVE_SIZE : MARKER_SIZE;
          const color = isInitialActive ? DARK : TEAL;
          createSvgMarker(svgUrl, color, size).then((dataUrl) => {
            if (dataUrl) {
              const totalW = size + 12;
              const totalH = size + 12 + 10;
              marker.setIcon({
                url: dataUrl,
                scaledSize: new google.maps.Size(totalW, totalH),
                anchor: new google.maps.Point(totalW / 2, totalH),
              });
            }
          });
        }

        marker.addListener('click', () => {
          setActiveGroupKey(String(group.id));
          goToCampus(getPrimary(group), group);
        });

        markersRef.current.push(marker);
      });

      if (groups.length > 1) {
        mapRef.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      } else if (groups.length === 1) {
        mapRef.current.setCenter({ lat: groups[0].latitude, lng: groups[0].longitude });
        mapRef.current.setZoom(13);
      }
    }

    initMap();
  }, [groups, googleMapsApiKey]);

  // Highlight active marker
  useEffect(() => {
    if (!window.google?.maps) return;

    const MARKER_SIZE = 48;
    const ACTIVE_SIZE = 56;

    const fallbackActive = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
        `<svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="${DARK}"/>
          <circle cx="16" cy="16" r="8" fill="white"/>
          <circle cx="16" cy="16" r="4" fill="${DARK}"/>
        </svg>`
      )}`,
      scaledSize: new google.maps.Size(38, 50),
      anchor: new google.maps.Point(19, 50),
    };
    const fallbackDefault = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
        `<svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="${TEAL}"/>
          <circle cx="16" cy="16" r="8" fill="white"/>
          <circle cx="16" cy="16" r="4" fill="${TEAL}"/>
        </svg>`
      )}`,
      scaledSize: new google.maps.Size(32, 42),
      anchor: new google.maps.Point(16, 42),
    };

    markersRef.current.forEach((marker, i) => {
      const group = groups[i];
      if (!group) return;
      const isActive = String(group.id) === activeGroupKey;
      const svgUrl = group.svg_url;

      if (svgUrl) {
        const size = isActive ? ACTIVE_SIZE : MARKER_SIZE;
        const color = isActive ? DARK : TEAL;
        createSvgMarker(svgUrl, color, size).then((dataUrl) => {
          if (dataUrl) {
            const totalW = size + 12;
            const totalH = size + 12 + 10;
            marker.setIcon({
              url: dataUrl,
              scaledSize: new google.maps.Size(totalW, totalH),
              anchor: new google.maps.Point(totalW / 2, totalH),
            });
          }
        });
      } else {
        marker.setIcon(isActive ? fallbackActive : fallbackDefault);
      }
      marker.setZIndex(isActive ? 999 : undefined);
    });
  }, [activeGroupKey, groups]);

  const handleSearch = useCallback(async () => {
    if (!zipCode.trim()) return;

    const coords = await geocodeZip(zipCode.trim());
    if (!coords) return;

    const { lat: userLat, lng: userLng } = coords;

    const filtered = locations.filter(
      (group) => haversineDistance(userLat, userLng, group.latitude, group.longitude) <= distance
    );

    filtered.sort((a, b) => {
      const dA = haversineDistance(userLat, userLng, a.latitude, a.longitude);
      const dB = haversineDistance(userLat, userLng, b.latitude, b.longitude);
      return dA - dB;
    });

    setFilteredLocations(filtered);

    if (mapRef.current) {
      if (filtered.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend({ lat: userLat, lng: userLng });
        filtered.forEach((group) => bounds.extend({ lat: group.latitude, lng: group.longitude }));
        mapRef.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      } else {
        mapRef.current.setCenter({ lat: userLat, lng: userLng });
        mapRef.current.setZoom(11);
      }
    }
  }, [zipCode, distance, locations]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // The campus page on woodsidebible.org. MP's slug keeps accents
  // ("troy-español") while WordPress strips them ("troy-espanol").
  const getLocationUrl = useCallback(
    (cong: Congregation) => {
      const slug = (cong.campus_slug ?? cong.name.toLowerCase().replace(/\s+/g, '-'))
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      return `${viewLocationBaseUrl.replace(/\/$/, '')}/${slug}/`;
    },
    [viewLocationBaseUrl]
  );

  // Rows and pins go straight to the campus page; a modifier click opens a
  // tab. A campus that is streaming asks first (stream or page), since the
  // LIVE pill is small and the stream is what most people want on a Sunday.
  const goToCampus = useCallback(
    (cong: Congregation, group: LocationGroup, e?: { metaKey?: boolean; ctrlKey?: boolean }) => {
      const url = getLocationUrl(cong);
      if (e?.metaKey || e?.ctrlKey) {
        window.open(url, '_blank', 'noopener');
        return;
      }
      if (liveByCampus.get(cong.id)?.isLive) {
        setLiveSheet({ cong, group });
        setLiveSheetOpen(true);
        return;
      }
      window.location.assign(url);
    },
    [getLocationUrl, liveByCampus]
  );

  return (
    <section className="font-sans">
      {/* Filter bar: zip search + radius + amenity chips, in the same
          rounded-pill language as the group and serve finders. */}
      <div className="flex flex-col gap-3 pb-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="Search by zip code"
              aria-label="Zip code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full rounded-full border border-gray-200 bg-white py-3 pr-4 pl-11 text-sm text-[#1c2b39] shadow-sm placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:flex-none">
              <select
                value={distance}
                onChange={(e) => setDistance(Number(e.target.value))}
                aria-label="Search radius"
                className="w-full cursor-pointer appearance-none rounded-full border border-gray-200 bg-white py-3 pr-10 pl-5 text-sm font-semibold text-[#1c2b39] shadow-sm focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none sm:w-36"
              >
                {DISTANCE_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} miles
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="inline-flex flex-1 shrink-0 items-center justify-center gap-2 rounded-full bg-[#299c8f] px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95 sm:flex-none"
            >
              <Search className="h-4 w-4" strokeWidth={2.5} />
              Search
            </button>
          </div>
        </div>

        {/* Chips: "Live now" while any campus is streaming, then the amenity toggles */}
        {(allAmenities.length > 0 || liveCount > 0) && (
          <div className="scrollbar-hide -mx-1 flex items-center gap-2 overflow-x-auto px-1 py-0.5">
            {liveCount > 0 && (
              <button
                type="button"
                aria-pressed={liveOnly}
                onClick={() => setLiveOnly((v) => !v)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full py-1.5 pr-3.5 pl-3 text-sm font-semibold whitespace-nowrap transition-all hover:scale-[1.03] active:scale-95 ${
                  liveOnly
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'bg-white text-[#1c2b39] ring-1 ring-gray-200 ring-inset hover:bg-gray-50'
                }`}
              >
                <span className="relative flex h-2 w-2">
                  <span
                    className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${liveOnly ? 'bg-white' : 'bg-red-500'}`}
                  />
                  <span
                    className={`relative inline-flex h-2 w-2 rounded-full ${liveOnly ? 'bg-white' : 'bg-red-600'}`}
                  />
                </span>
                Live now
                <span className={liveOnly ? 'text-white/60' : 'text-gray-400'}>({liveCount})</span>
              </button>
            )}
            {allAmenities.map((amenity) => {
              const isActive = activeAmenityFilters.has(amenity.id);
              const detail = legendDetails.get(amenity.id);
              return (
                <button
                  key={amenity.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => {
                    setActiveAmenityFilters((prev) => {
                      const next = new Set(prev);
                      if (next.has(amenity.id)) next.delete(amenity.id);
                      else next.add(amenity.id);
                      return next;
                    });
                  }}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1.5 pr-3.5 pl-2 text-sm font-semibold whitespace-nowrap transition-all hover:scale-[1.03] active:scale-95 ${
                    isActive
                      ? 'bg-[#1c2b39] text-white shadow-sm'
                      : 'bg-white text-[#1c2b39] ring-1 ring-gray-200 ring-inset hover:bg-gray-50'
                  }`}
                >
                  <AmenityBadge amenity={amenity} light={isActive} />
                  {amenity.name}
                  {detail && (
                    <span className={isActive ? 'text-white/60' : 'text-gray-400'}>({detail})</span>
                  )}
                </button>
              );
            })}
            {(activeAmenityFilters.size > 0 || liveOnly) && (
              <button
                type="button"
                onClick={() => {
                  setActiveAmenityFilters(new Set<number>());
                  setLiveOnly(false);
                }}
                className="shrink-0 px-2 text-xs font-semibold text-gray-500 hover:text-[#1c2b39]"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mobile list / map toggle */}
      <div className="pb-3 md:hidden">
        <div className="flex rounded-full bg-gray-100 p-1">
          {(
            [
              { key: 'list', label: 'List', Icon: List },
              { key: 'map', label: 'Map', Icon: MapIcon },
            ] as const
          ).map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              aria-pressed={mobileView === key}
              onClick={() => setMobileView(key)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold transition-all ${
                mobileView === key ? 'bg-[#1c2b39] text-white shadow-sm' : 'text-gray-500'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content.
          Fixed height only from md up, where the list scrolls in its own pane
          beside the map. On a phone the widget grows to fit every campus and the
          page scrolls normally: an inner scroller there fought the page for the
          same swipe, so you'd get stuck inside the list trying to scroll past it. */}
      <div className="flex overflow-hidden rounded-2xl border border-gray-200 md:h-[600px]">
        {/* Location List — hidden on mobile map view */}
        <div
          className={`relative w-full shrink-0 md:w-[50%] ${
            mobileView === 'map' ? 'hidden md:block' : ''
          }`}
        >
          <div ref={listRef} className="scrollbar-hide md:h-full md:overflow-y-auto">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="border-b border-gray-200 px-5 pt-4 pb-3 md:px-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
                    <div className="h-6 w-36 animate-pulse rounded bg-gray-200" />
                    <div className="ml-auto h-9 w-9 animate-pulse rounded-full bg-gray-200" />
                  </div>
                  <div className="mt-3 ml-[52px] flex items-center gap-3">
                    <div className="h-3 w-8 animate-pulse rounded bg-gray-100" />
                    <div className="h-3.5 w-40 animate-pulse rounded bg-gray-200" />
                  </div>
                  <div className="mt-3 flex items-center gap-4 border-t border-gray-100 pt-2.5">
                    <div className="h-3 w-52 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-28 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))
            ) : groups.length === 0 ? (
              <div className="flex h-full items-center justify-center text-center">
                <div>
                  <MapPin className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <p className="text-lg font-semibold text-gray-500">No campuses found</p>
                  <p className="mt-1 text-sm text-gray-400">
                    {liveOnly || activeAmenityFilters.size > 0
                      ? 'Try clearing a filter or widening your search'
                      : 'Try increasing your search radius'}
                  </p>
                </div>
              </div>
            ) : (
              groups.map((group) => {
                const primary = getPrimary(group);
                const subCongregations = getSubCongregations(group);
                const hasSelected =
                  homeCampusId != null && group.congregations.some((c) => c.id === homeCampusId);

                return (
                  <React.Fragment key={group.id}>
                    {hasSelected && (
                      <div className="flex items-center gap-3 px-5 pt-3 pb-1 md:px-6">
                        <span
                          className="text-[10px] font-bold tracking-wider whitespace-nowrap uppercase"
                          style={{ color: TEAL }}
                        >
                          Your campus
                        </span>
                        <div className="h-px flex-1" style={{ backgroundColor: `${TEAL}30` }} />
                      </div>
                    )}
                    <div
                      id={`location-group-${group.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => goToCampus(primary, group, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToCampus(primary, group, e);
                        }
                      }}
                      className={`group/row cursor-pointer border-b border-gray-200 px-5 pt-4 pb-3 transition-colors md:px-6 ${
                        hasSelected ? 'bg-[#299c8f]/[0.06]' : 'hover:bg-gray-50/60'
                      }`}
                    >
                      <CongregationRow
                        cong={primary}
                        svgUrl={group.svg_url}
                        isSelected={primary.id === homeCampusId}
                        live={liveByCampus.get(primary.id)}
                        amenityFilters={activeAmenityFilters}
                        onSelect={() => handleSetHome(primary.id)}
                      />

                      {/* Special service (replaces normal times this week) */}
                      {primary.special_service && (
                        <div className="mt-3 ml-[52px] rounded-xl bg-gray-100 p-4">
                          <span
                            className="text-[10px] font-bold tracking-wider uppercase"
                            style={{ color: TEAL }}
                          >
                            This Week
                          </span>
                          <div className="mt-1 flex items-center gap-3">
                            {primary.special_service.image_url && (
                              <div
                                className="w-20 flex-shrink-0 overflow-hidden rounded-lg"
                                style={{ aspectRatio: '16/9' }}
                              >
                                <img
                                  src={primary.special_service.image_url}
                                  alt={primary.special_service.name}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <span
                                className="text-xs font-bold tracking-wide uppercase"
                                style={{ color: DARK }}
                              >
                                {primary.special_service.name}
                              </span>
                              {primary.special_service.description && (
                                <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                                  {primary.special_service.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="mt-2.5">
                            <ServiceTimeLine
                              services={primary.special_service.services}
                              amenityFilters={activeAmenityFilters}
                            />
                          </div>
                          {primary.special_service.address !== group.address && (
                            <p className="mt-2 text-[12px] text-gray-400">
                              {primary.special_service.venue_name && (
                                <span className="font-semibold text-gray-500">
                                  {primary.special_service.venue_name}:{' '}
                                </span>
                              )}
                              {primary.special_service.address}, {primary.special_service.city},{' '}
                              {primary.special_service.state} {primary.special_service.zip}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Other congregations at this location (Español) */}
                      {subCongregations.map((cong) => (
                        <div
                          key={cong.id}
                          className="mt-2 border-t border-gray-100 pt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToCampus(cong, group, e);
                          }}
                        >
                          <CongregationRow
                            cong={cong}
                            svgUrl={null}
                            isSelected={cong.id === homeCampusId}
                            live={liveByCampus.get(cong.id)}
                            amenityFilters={activeAmenityFilters}
                            onSelect={() => handleSetHome(cong.id)}
                            nested
                          />
                          {cong.special_service && (
                            <div className="mt-2 ml-[52px] rounded-xl bg-gray-100 p-3">
                              <span
                                className="text-[10px] font-bold tracking-wider uppercase"
                                style={{ color: TEAL }}
                              >
                                This Week: {cong.special_service.name}
                              </span>
                              <div className="mt-1.5">
                                <ServiceTimeLine
                                  services={cong.special_service.services}
                                  amenityFilters={activeAmenityFilters}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* The location's own details, shared by every congregation above */}
                      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-2.5 text-[12px] text-gray-500">
                        {group.address && (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            {group.address}, {group.city}, {group.state} {group.zip.split('-')[0]}
                          </span>
                        )}
                        {group.phone && (
                          <a
                            href={`tel:${group.phone}`}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            {group.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>
          <ListScrollIndicator containerRef={listRef} />
        </div>

        {/* Map — hidden on mobile list view. Needs its own height on mobile now
            that the row no longer sets one; a map with auto height collapses. */}
        <div
          className={`h-[600px] flex-1 md:h-full ${mobileView === 'list' ? 'hidden md:block' : ''}`}
        >
          {!googleMapsApiKey ? (
            <div className="flex h-full items-center justify-center bg-gray-100">
              <div className="text-center">
                <MapPin className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">Google Maps API key required</p>
                <p className="mt-1 text-xs text-gray-400">
                  Set <code>googleMapsApiKey</code> to enable the map
                </p>
              </div>
            </div>
          ) : (
            <div ref={mapContainerRef} className="h-full w-full" />
          )}
        </div>
      </div>

      {/* A live campus asked: stream or page */}
      {liveSheet && (
        <LiveSheet
          open={liveSheetOpen && !!liveByCampus.get(liveSheet.cong.id)?.isLive}
          onClose={() => setLiveSheetOpen(false)}
          cong={liveSheet.cong}
          svgUrl={liveSheet.group.svg_url}
          address={`${liveSheet.group.address}, ${liveSheet.group.city}, ${liveSheet.group.state} ${liveSheet.group.zip}`}
          watchUrl={liveByCampus.get(liveSheet.cong.id)?.watchUrl ?? ''}
          pageUrl={getLocationUrl(liveSheet.cong)}
        />
      )}

      {fetchError && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm font-medium text-gray-600">Unable to load locations</p>
          <p className="mt-1 text-xs text-gray-400">{fetchError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-full bg-gray-100 px-4 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Try Again
          </button>
        </div>
      )}
    </section>
  );
}
